import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, MessageSquareText, HardHat, AlertTriangle, Recycle, Stethoscope,
  Users as UsersIcon, FolderOpen, ShieldCheck, Menu, Search, Bell, LogOut,
  ChevronDown, Settings, FlaskConical, Building2, Leaf, GraduationCap, ClipboardCheck, Workflow, BarChart3, BookOpen, ScrollText, Vote, FileSignature, PackageSearch, Factory, Scale,
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

function AlertsBell() {
  const [data, setData] = useState({ count: 0, expired: 0, alerts: [] });
  const navigate = useNavigate();
  useEffect(() => {
    let m = true;
    const load = () => api.get("/alerts").then(({ data }) => m && setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { m = false; clearInterval(t); };
  }, []);
  const routeMap = { documentos: "/documentos", epis: "/epis", medicina: "/medicina", iso14001: "/iso14001", licencas: "/licencas", treinamentos: "/treinamentos" };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100" data-testid="notifications-btn">
        <Bell className="h-5 w-5" />
        {data.count > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{data.count}</span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Alertas de vencimento</span>
          {data.expired > 0 && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">{data.expired} vencido(s)</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.alerts.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-slate-400">Nenhum vencimento próximo 🎉</div>
        ) : (
          data.alerts.slice(0, 12).map((a, i) => (
            <DropdownMenuItem key={i} onClick={() => navigate(routeMap[a.module] || "/")} data-testid={`alert-${i}`} className="flex flex-col items-start gap-0.5">
              <div className="flex w-full items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">{a.label}</span>
                <span className={`text-[10px] font-bold ${a.level === "vencido" ? "text-rose-600" : "text-amber-600"}`}>
                  {a.level === "vencido" ? `Vencido há ${-a.days}d` : `Vence em ${a.days}d`}
                </span>
              </div>
              <span className="w-full truncate text-xs text-slate-500">{a.title}</span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const SECTORS = ["Todos", "Usinagem", "Testes", "Pintura", "Instrumentacao", "Montagem", "Qualidade", "Caldeiraria", "Administrativo", "Geral"];

const NAV = [
  { section: "Visão Geral", items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  {
    section: "Segurança do Trabalho",
    items: [
      { to: "/dds", label: "DDS", icon: MessageSquareText },
      { to: "/epis", label: "EPIs (NR-06)", icon: HardHat },
      { to: "/epi-catalogo", label: "Catálogo de EPI", icon: PackageSearch },
      { to: "/ocorrencias", label: "Ocorrências", icon: AlertTriangle },
      { to: "/treinamentos", label: "Treinamentos", icon: GraduationCap },
      { to: "/inspecoes", label: "Inspeções", icon: ClipboardCheck },
      { to: "/cipa", label: "CIPA (Eleição)", icon: Vote },
      { to: "/ordens-servico", label: "Ordem de Serviço", icon: FileSignature },
    ],
  },
  {
    section: "Meio Ambiente",
    items: [
      { to: "/residuos", label: "Resíduos", icon: Recycle },
      { to: "/iso14001", label: "Gestão ISO 14001", icon: Leaf },
      { to: "/aspectos", label: "Aspectos e Impactos", icon: Workflow },
      { to: "/licencas", label: "Licenças", icon: ScrollText },
      { to: "/requisitos-legais", label: "Requisitos Legais", icon: Scale },
      { to: "/painel-ambiental", label: "Painel Ambiental", icon: BarChart3 },
    ],
  },
  {
    section: "Medicina do Trabalho",
    items: [{ to: "/medicina", label: "Exames / ASO", icon: Stethoscope }],
  },
  {
    section: "Gestão",
    items: [
      { to: "/colaboradores", label: "Colaboradores", icon: UsersIcon },
      { to: "/portais", label: "Portais", icon: Factory },
      { to: "/documentos", label: "GED / Documentos", icon: FolderOpen },
    ],
  },
];

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState("Todos");
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const doLogout = async () => {
    await logout();
    toast.success("Sessão encerrada");
    navigate("/login");
  };

  const nav = [...NAV];
  if (isAdmin) {
    nav.push({ section: "Administração", items: [{ to: "/usuarios", label: "Usuários", icon: ShieldCheck }, { to: "/configuracoes", label: "Configurações", icon: Settings }] });
  }

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-4">
        <img src="/vigia-logo.png" alt="Valbrac" className="h-11 w-auto shrink-0 object-contain" data-testid="sidebar-logo" />
        {!collapsed && (
          <div className="leading-tight">
            <p className="text-sm font-extrabold tracking-tight text-white">VALBRAC</p>
            <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-400">Portal SSMA</p>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {nav.map((group) => (
          <div key={group.section} className="mb-4">
            {!collapsed && (
              <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {group.section}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "border-l-4 border-emerald-400 bg-[#134074] text-white"
                        : "border-l-4 border-transparent text-slate-300 hover:bg-[#134074]/60 hover:text-white"
                    }`
                  }
                >
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        {!collapsed && (
          <div className="rounded-lg bg-[#134074]/50 p-3">
            <div className="flex items-center gap-1.5 text-xs text-emerald-300">
              <ShieldCheck className="h-3.5 w-3.5" /> ISO 45001 · 14001 · 9001
            </div>
            <p className="mt-1 text-[10px] text-slate-400">NR-01 · NR-06 · NR-12 · NR-13</p>
          </div>
        )}
        {!collapsed && (
          <p className="mt-2 text-center text-[10px] text-slate-500" data-testid="dev-credit">
            Desenvolvido por <span className="font-semibold text-emerald-400">Leandro Américo</span>
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside
        className={`hidden shrink-0 bg-[#0B2545] transition-all duration-300 lg:block ${collapsed ? "w-20" : "w-64"}`}
      >
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-[#0B2545]">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <header className="z-30 flex h-16 shrink-0 items-center gap-2 border-b border-[#E2E8F0] bg-white px-3 sm:px-5">
          <button
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
            onClick={() => {
              setCollapsed((c) => !c);
              setMobileOpen((o) => !o);
            }}
            data-testid="sidebar-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="relative hidden max-w-md flex-1 sm:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar (DDS, EPI, ocorrências, documentos...)"
              className="h-9 border-[#E2E8F0] bg-slate-50 pl-9 text-sm"
              data-testid="global-search"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:block">
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger className="h-9 w-[170px] border-[#E2E8F0] text-sm" data-testid="sector-selector">
                  <Building2 className="mr-1 h-4 w-4 text-[#134074]" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s}>{s === "Todos" ? "Todos os setores" : s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <button onClick={() => navigate("/tutorial")} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100" title="Tutorial" data-testid="help-btn">
              <BookOpen className="h-5 w-5" />
            </button>
            <AlertsBell />

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-100" data-testid="user-menu">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#134074] text-xs font-bold text-white">
                  {initials}
                </div>
                <div className="hidden text-left leading-tight sm:block">
                  <p className="max-w-[140px] truncate text-sm font-semibold text-slate-800">{user?.name}</p>
                  <p className="text-[11px] text-slate-500">{ROLE_LABELS[user?.role]}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="truncate text-sm">{user?.email}</p>
                  <p className="text-xs font-normal text-slate-500">{ROLE_LABELS[user?.role]} · {user?.department}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/usuarios")} data-testid="menu-users">
                    <UsersIcon className="mr-2 h-4 w-4" /> Gerenciar usuários
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet context={{ search, sector }} />
        </main>
      </div>
    </div>
  );
}
